import React, { FunctionComponent, useEffect, useReducer } from 'react'
import QuizzLogo from './QuizzLogo'
import { getStyleForImage, getStyleForImageContainer, ratio } from '../helpers'
import TSConstants from '../../../utils/TSConstants'
import { PROGRESS_BAR_BOTTOM, PROGRESS_BAR_TOP } from '../constants'
import { tt } from '../../../i18n'
import RotateDisk from '../../../components/RotateDisk'
import WheelButton from '../../../components/cta/WheelButton'
import { useSelector } from 'react-redux'
import {
  currentQuestionIdxSelector,
  currentQuestionSelector,
  quizzSelector,
} from '../../../redux/selectors/quizz'
import { lotteryConfigSelector } from '../../../redux/selectors/lottery'
import { ProgressBar } from 'react-bootstrap'
import { AspectRatio } from '../../../utils/aspect_ratio'
import { timerInitialState } from '../reducers/timerReducer'
import { gameInitialState } from '../reducers/gameReducer'

interface Props {
  progressBarPos: number
  timer: typeof timerInitialState
  game: typeof gameInitialState
  onClickPlayAudio: () => void
  answerWithIndex: (index: number) => void
  answerIsCorrect: boolean
}

const Quizz: FunctionComponent<Props> = ({
  progressBarPos,
  timer,
  game,
  onClickPlayAudio,
  answerWithIndex,
  answerIsCorrect,
}) => {
  const currentQuestion = useSelector(currentQuestionSelector)
  const currentQuestionIdx = useSelector(currentQuestionIdxSelector)
  const lotteryConfig = useSelector(lotteryConfigSelector)
  const quizz = useSelector(quizzSelector)

  const [progress, setProgress] = React.useState(0)
  const [pictureHeight, setPictureHeight] = React.useState('0px')

  useEffect(() => {
    switch (ratio) {
      case AspectRatio._18_9:
        setPictureHeight('28vh')
        break
      case AspectRatio._19_9:
        setPictureHeight('30vh')
        break
      default:
        break
    }
  }, [])

  useEffect(() => {
    setProgress(
      timer.remainingSeconds * (100 / (lotteryConfig?.qTimerSeconds ?? 0))
    )
  }, [timer.remainingSeconds, lotteryConfig?.qTimerSeconds])

  return (
    <>
      <QuizzLogo
        containerClass={'logo-container logo-container-centered'}
        useConfig={true}
      />

      <div className="row w-margin-bottom-half">
        <div className="col s12">
          <p
            className="text-center q-question-text"
            dangerouslySetInnerHTML={{
              __html:
                currentQuestion && !game.answered
                  ? currentQuestion.text
                  : '&nbsp;',
            }}
          />
        </div>
      </div>
      {currentQuestion && !currentQuestion.audio && !!currentQuestion.img && (
        <div
          className="d-flex justify-content-center align-items-center w-margin-bottom-half"
          style={getStyleForImageContainer()}
        >
          <img
            id={`q-img-${currentQuestionIdx}`}
            className="twic"
            src={TSConstants.PLACEHOLDER_URL}
            data-src={`image:/cdm/img/quizz/${currentQuestion.img}`}
            style={getStyleForImage()}
            alt="quizz-img"
          />
        </div>
      )}
      {progressBarPos === PROGRESS_BAR_TOP && (
        <>
          <div className="middle-zone">
            {!game.answered && timer.isRunning && (
              <div className="quizz-progress-bar-top">
                {/*{renderProgressBar()}*/}
                <ProgressBar
                  now={progress}
                  animated={false}
                  variant={progress <= 30 ? 'danger' : 'success'}
                />
              </div>
            )}
            {!timer.isRunning && game.answered && (
              <div className="d-flex align-items-center">
                {answerIsCorrect && (
                  <span className="animate-answer">
                    {tt('quizz_good_answer')}&nbsp;✅
                  </span>
                )}
                {!answerIsCorrect && !game.hasTimedOut && (
                  <span className="animate-answer">
                    {tt('quizz_wrong_answer')}&nbsp;❌
                  </span>
                )}
                {!answerIsCorrect && game.hasTimedOut && (
                  <span className="animate-answer">
                    {tt('quizz_time_elapsed')}&nbsp;⏱
                  </span>
                )}
              </div>
            )}
            {currentQuestion?.audio && (
              <RotateDisk
                audio={currentQuestion?.audio}
                containerHeight={pictureHeight}
                isStart={false}
                onClickPlay={() => onClickPlayAudio()}
                resetCounter={currentQuestionIdx}
                hide={
                  timer.isRunning || game.answered || !currentQuestion?.audio
                }
              />
            )}
          </div>
        </>
      )}
      {progressBarPos === PROGRESS_BAR_BOTTOM && (
        <div className="w-margin-bottom-half quizz-progress-bar-top">
          {/*{!answered && renderProgressBar()}*/}
          {!game.answered && (
            <ProgressBar
              now={progress}
              animated={false}
              variant={progress <= 30 ? 'danger' : 'success'}
            />
          )}
        </div>
      )}

      <div
        className="row w-margin-bottom all-answer"
        style={{ marginLeft: 0, marginRight: 0 }}
      >
        {currentQuestion && currentQuestion.answers && (
          <>
            {currentQuestion.answers.map((answer: string, idx: number) => {
              let containerClazz: string
              let btnClazz: string
              let imgClazz: string

              let isCorrectAnswer: boolean
              let shouldFrameAnswer: boolean

              if (quizz?.multi) {
                if (game.animatePostAnswer) {
                  shouldFrameAnswer = !!(
                    !currentQuestion?.answersIdx?.includes(idx) &&
                    currentQuestion?.clientSideAnswersIdx?.includes(idx)
                  )

                  isCorrectAnswer = !!currentQuestion?.answersIdx?.includes(idx)
                } else {
                  shouldFrameAnswer = false
                  isCorrectAnswer =
                    !!currentQuestion?.clientSideAnswersIdx?.includes(idx)
                }
              } else {
                isCorrectAnswer =
                  game.animatePostAnswer && idx === currentQuestion?.answerIdx
                shouldFrameAnswer =
                  game.animatePostAnswer && idx === game.answeredIndex
              }

              if (isCorrectAnswer) {
                containerClazz = ''
                btnClazz = 'quizz-good-answer-btn'
                imgClazz = 'quizz-good-answer-img'
              } else {
                if (shouldFrameAnswer) {
                  containerClazz = 'quizz-correct'
                  btnClazz = ''
                  imgClazz = 'quizz-bad-answer-img'
                } else {
                  containerClazz = ''
                  btnClazz = ''
                  imgClazz = ''
                }
              }

              return (
                <div
                  key={`ans-${idx}`}
                  className={`quizz-answer-container ${
                    (currentQuestion?.answers.length ?? 0) > 3
                      ? 'col-6'
                      : 'col-12'
                  }`}
                  style={{ padding: 0 }}
                >
                  {currentQuestion?.hasImgAnswers && (
                    <span
                      className={`d-flex w-margin-bottom-third justify-content-center ${imgClazz}`}
                      onClick={() => answerWithIndex(idx)}
                    >
                      <img
                        className={`twic`}
                        src={TSConstants.PLACEHOLDER_URL}
                        data-src={`image:/cdm/img/quizz/${answer}`}
                        style={{ height: '10vh' }}
                      />
                    </span>
                  )}
                  {!currentQuestion?.hasImgAnswers && (
                    <WheelButton
                      className={`${btnClazz}`}
                      containerClassName={`quizz-btn ${containerClazz}`}
                      text={answer}
                      isAnwsers={true}
                      isDisplay={game.isDisplayAnswers}
                      onClick={() => answerWithIndex(idx)}
                    />
                  )}
                </div>
              )
            })}
          </>
        )}
      </div>
    </>
  )
}

export default Quizz
